"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SingleValueMonitoringChart = void 0;
/*
 * Copyright 2025, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const react_1 = __importDefault(require("react"));
const HashLoader_1 = __importDefault(require("react-spinners/HashLoader"));
const react_i18next_1 = require("react-i18next");
// Component to display a single value fetched from an API endpoint
const SingleValueMonitoringChart = ({ apiUrl, valueName, valueTitle, valueUnit }) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const [value, setValue] = react_1.default.useState(undefined);
    const [errorKey, setErrorKey] = react_1.default.useState(null); // Translation key for error message, if any
    const [loading, setLoading] = react_1.default.useState(true);
    react_1.default.useEffect(() => {
        const abortController = new AbortController(); // Create an AbortController to manage fetch cancellation
        setLoading(true);
        setErrorKey(null);
        // Fetch the value from the provided API endpoint
        fetch(apiUrl, {
            method: 'POST',
            body: JSON.stringify({ refreshCache: false }),
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            signal: abortController.signal // Attach the signal to the fetch request
        })
            .then((response) => {
            if (response.status === 200) {
                response
                    .json()
                    .then((jsonData) => {
                    // Extract the value using the provided valueName
                    const extractedValue = jsonData[valueName];
                    if (typeof extractedValue === 'number') {
                        setValue(extractedValue);
                    }
                    else {
                        setErrorKey('admin:monitoring.errors.invalidValueType');
                        console.error(t('admin:monitoring.errors.invalidValueType'), 'Expected number, got:', typeof extractedValue, extractedValue);
                    }
                })
                    .catch((err) => {
                    if (err.name !== 'AbortError') {
                        // Ignore abort errors
                        setErrorKey('admin:monitoring.errors.jsonConversion');
                        console.error(t('admin:monitoring.errors.jsonConversion'), err);
                    }
                });
            }
            else if (response.status === 500) {
                setErrorKey('admin:monitoring.errors.serverError');
                console.error(t('admin:monitoring.errors.serverError'), response);
            }
            else {
                setErrorKey('admin:monitoring.errors.fetchError');
                console.error(t('admin:monitoring.errors.fetchError'), response);
            }
        })
            .catch((err) => {
            if (err.name !== 'AbortError') {
                setErrorKey('admin:monitoring.errors.fetchError');
                console.error(t('admin:monitoring.errors.fetchError'), err);
            }
        })
            .finally(() => {
            if (!abortController.signal.aborted) {
                setLoading(false); // Ensure loading is set to false after fetch completes
            }
        });
        return () => abortController.abort(); // Cleanup function to abort fetch on unmount
    }, [apiUrl, valueName]);
    // Render the value in a styled container
    // Only show decimals if the value is not an integer, else show as integer.
    // This ensures clean display for both integer and decimal values.
    return (react_1.default.createElement("section", { className: "monitoring-single-value-container" },
        react_1.default.createElement("div", null,
            react_1.default.createElement("h3", null, valueTitle)),
        errorKey ? (react_1.default.createElement("div", { className: "monitoring-error" }, t(errorKey))) : loading ? (react_1.default.createElement(HashLoader_1.default, { size: '30px', color: '#aaaaaa', loading: true })) : (react_1.default.createElement("div", { className: "monitoring-big-number" },
            value !== undefined ? (Number.isInteger(value) ? value : Number(value).toFixed(1)) : '',
            valueUnit ? react_1.default.createElement("span", null, valueUnit) : null))));
};
exports.SingleValueMonitoringChart = SingleValueMonitoringChart;
//# sourceMappingURL=SingleValueMonitoringChart.js.map